/**
 * Floating bottom bar that accumulates document revision/request items.
 * Shows count, expandable list, and "Review & Send" button that opens
 * a full summary screen before sending.
 */
import React, { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Button,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Slide,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  Send as SendIcon,
  Close as CloseIcon,
  Delete as RemoveIcon,
  Add as AddIcon,
  Email as EmailIcon,
  Link as LinkIcon,
  ExpandLess as CollapseIcon,
  ExpandMore as ExpandIcon,
  CheckCircle as CheckIcon,
} from "@mui/icons-material";
import { tokens } from "@/tokens";

export interface DocumentRequestItem {
  id: string;
  slotId?: string;
  label: string;
  description?: string;
  reason: string;
  requirements: string[];
  linkedIssues?: Array<{ itemId: string; status: string; detail?: string }>;
}

interface DocumentRequestBarProps {
  requests: DocumentRequestItem[];
  onRemove: (id: string) => void;
  onClear: () => void;
  onSend: (notifyEmail?: string) => Promise<void>;
  onAddCustom: (item: Omit<DocumentRequestItem, "id">) => void;
  portalLink?: string;
  sending?: boolean;
  /** slotIds that already have pending requests in the DB */
  existingRequestSlotIds?: Set<string>;
}

export const DocumentRequestBar: React.FC<DocumentRequestBarProps> = ({
  requests,
  onRemove,
  onClear,
  onSend,
  onAddCustom,
  portalLink,
  sending,
  existingRequestSlotIds,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [notifyEnabled, setNotifyEnabled] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [customLabel, setCustomLabel] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [customReqs, setCustomReqs] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);
  const [sent, setSent] = useState(false);

  if (requests.length === 0 && !sent) return null;

  const handleSend = async () => {
    await onSend(notifyEnabled && notifyEmail.trim() ? notifyEmail.trim() : undefined);
    setSent(true);
    setTimeout(() => {
      setReviewOpen(false);
      setSent(false);
      setNotifyEmail("");
      setNotifyEnabled(false);
    }, 1500);
  };

  const handleAddCustom = () => {
    if (!customLabel.trim() || !customReason.trim()) return;
    onAddCustom({
      label: customLabel.trim(),
      reason: customReason.trim(),
      requirements: customReqs.split("\n").map((r) => r.trim()).filter(Boolean),
    });
    setCustomLabel("");
    setCustomReason("");
    setCustomReqs("");
    setCustomOpen(false);
  };

  const handleCopyLink = () => {
    if (portalLink) {
      navigator.clipboard.writeText(portalLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  // Dedup check
  const hasDuplicate = (slotId?: string) =>
    slotId && existingRequestSlotIds?.has(slotId);

  const duplicateCount = requests.filter((r) => hasDuplicate(r.slotId)).length;

  return (
    <>
      {/* Floating bottom bar */}
      <Slide direction="up" in={requests.length > 0} mountOnEnter unmountOnExit>
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1200,
            bgcolor: tokens.bg.surface,
            borderTop: `2px solid ${tokens.brand.primary}`,
            boxShadow: "0 -4px 20px rgba(0,0,0,0.12)",
          }}
        >
          {expanded && (
            <Box sx={{ maxHeight: 280, overflow: "auto", px: 3, py: 1.5 }}>
              <Stack spacing={1}>
                {requests.map((req) => (
                  <Stack
                    key={req.id}
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    sx={{
                      py: 0.75,
                      px: 1.5,
                      bgcolor: hasDuplicate(req.slotId) ? `${tokens.status.warning}08` : tokens.bg.muted,
                      borderRadius: tokens.radius.sm,
                      border: `1px solid ${hasDuplicate(req.slotId) ? `${tokens.status.warning}30` : tokens.border.light}`,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Typography sx={{ fontSize: tokens.fontSize.xs, fontWeight: tokens.fontWeight.semibold, color: tokens.text.primary }}>
                          {req.label}
                        </Typography>
                        {hasDuplicate(req.slotId) && (
                          <Chip label="Already requested" size="small" sx={{ height: 16, fontSize: 9, color: tokens.status.warning, borderColor: tokens.status.warning }} variant="outlined" />
                        )}
                      </Stack>
                      <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {req.reason}
                      </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => onRemove(req.id)} sx={{ color: tokens.text.muted, "&:hover": { color: tokens.status.danger } }}>
                      <RemoveIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Stack>
                ))}
              </Stack>
              <Button
                size="small"
                startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                onClick={() => setCustomOpen(true)}
                sx={{ mt: 1, textTransform: "none", fontSize: tokens.fontSize["2xs"], color: tokens.brand.primary }}
              >
                Add Custom Document Request
              </Button>
            </Box>
          )}

          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 3, py: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <IconButton size="small" onClick={() => setExpanded(!expanded)} sx={{ color: tokens.text.secondary }}>
                {expanded ? <CollapseIcon /> : <ExpandIcon />}
              </IconButton>
              <Chip
                label={`${requests.length} document request${requests.length > 1 ? "s" : ""}`}
                sx={{ bgcolor: `${tokens.brand.primary}12`, color: tokens.brand.primary, fontWeight: tokens.fontWeight.semibold, fontSize: tokens.fontSize.xs, height: 28 }}
              />
              {duplicateCount > 0 && (
                <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.status.warning }}>
                  {duplicateCount} already requested
                </Typography>
              )}
              <Button size="small" onClick={onClear} sx={{ textTransform: "none", fontSize: tokens.fontSize["2xs"], color: tokens.text.muted }}>
                Clear all
              </Button>
            </Stack>
            <Button
              variant="contained"
              startIcon={<SendIcon sx={{ fontSize: 16 }} />}
              onClick={() => setReviewOpen(true)}
              sx={{
                textTransform: "none", fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.semibold,
                bgcolor: tokens.brand.primary, borderRadius: tokens.radius.md, boxShadow: "none", px: 3,
                "&:hover": { bgcolor: tokens.brand.primaryHover, boxShadow: "none" },
              }}
            >
              Review & Send
            </Button>
          </Stack>
        </Box>
      </Slide>

      {/* ─── Review & Send Summary Modal ─────────────────────────── */}
      <Dialog
        open={reviewOpen}
        onClose={() => !sending && setReviewOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: tokens.radius.lg, maxHeight: "85vh" } }}
      >
        <DialogTitle sx={{ fontSize: tokens.fontSize.lg, fontWeight: tokens.fontWeight.heavy, color: tokens.text.primary, pb: 0 }}>
          Review Document Requests
        </DialogTitle>
        <DialogContent>
          {sent ? (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <CheckIcon sx={{ fontSize: 48, color: tokens.status.success, mb: 1 }} />
              <Typography sx={{ fontSize: tokens.fontSize.md, fontWeight: tokens.fontWeight.bold, color: tokens.text.primary }}>
                Requests Sent Successfully
              </Typography>
              <Typography sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.muted, mt: 0.5 }}>
                {notifyEnabled && notifyEmail.trim() ? `Notification sent to ${notifyEmail}` : "Requests saved to the supplier portal"}
              </Typography>
            </Box>
          ) : (
            <>
              <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.muted, mt: 0.5, mb: 2 }}>
                Review the {requests.length} document request{requests.length > 1 ? "s" : ""} below before sending to the supplier portal.
              </Typography>

              {/* Request Summary Cards */}
              <Stack spacing={1.5} mb={3}>
                {requests.map((req, idx) => (
                  <Box
                    key={req.id}
                    sx={{
                      border: `1px solid ${hasDuplicate(req.slotId) ? `${tokens.status.warning}40` : tokens.border.container}`,
                      borderRadius: tokens.radius.md,
                      overflow: "hidden",
                      bgcolor: hasDuplicate(req.slotId) ? `${tokens.status.warning}04` : tokens.bg.surface,
                    }}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.25, bgcolor: tokens.bg.muted, borderBottom: `1px solid ${tokens.border.light}` }}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Chip label={`${idx + 1}`} size="small" sx={{ width: 24, height: 24, fontSize: 11, fontWeight: 700, bgcolor: tokens.brand.primary, color: "#fff" }} />
                        <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.semibold, color: tokens.text.primary }}>
                          {req.label}
                        </Typography>
                        {hasDuplicate(req.slotId) && (
                          <Chip label="Previously requested" size="small" variant="outlined" sx={{ height: 20, fontSize: 10, color: tokens.status.warning, borderColor: tokens.status.warning }} />
                        )}
                        {!req.slotId && (
                          <Chip label="Custom" size="small" sx={{ height: 20, fontSize: 10, bgcolor: `${tokens.brand.primary}12`, color: tokens.brand.primary }} />
                        )}
                      </Stack>
                      <IconButton size="small" onClick={() => onRemove(req.id)} sx={{ color: tokens.text.muted }}>
                        <CloseIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Stack>
                    <Box sx={{ px: 2, py: 1.5 }}>
                      {req.linkedIssues && req.linkedIssues.length > 0 && (
                        <>
                          <Typography sx={{ fontSize: tokens.fontSize["2xs"], fontWeight: tokens.fontWeight.semibold, color: tokens.text.muted, mb: 0.5, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                            Linked Validation Issues
                          </Typography>
                          <Stack direction="row" flexWrap="wrap" gap={0.5} mb={1.25}>
                            {req.linkedIssues.map((issue) => (
                              <Chip
                                key={issue.itemId}
                                label={issue.detail || issue.itemId.replace(/_/g, " ")}
                                size="small"
                                sx={{
                                  height: 22,
                                  fontSize: 10,
                                  maxWidth: 280,
                                  bgcolor: `${issue.status === "FAIL" ? tokens.status.danger : tokens.status.warning}10`,
                                  color: issue.status === "FAIL" ? tokens.status.danger : tokens.status.warning,
                                  border: `1px solid ${issue.status === "FAIL" ? tokens.status.danger : tokens.status.warning}30`,
                                }}
                              />
                            ))}
                          </Stack>
                        </>
                      )}
                      <Typography sx={{ fontSize: tokens.fontSize["2xs"], fontWeight: tokens.fontWeight.semibold, color: tokens.text.muted, mb: 0.25, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        Reason
                      </Typography>
                      <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.body, lineHeight: 1.6, mb: req.requirements.length > 0 ? 1.25 : 0 }}>
                        {req.reason}
                      </Typography>
                      {req.requirements.length > 0 && (
                        <>
                          <Typography sx={{ fontSize: tokens.fontSize["2xs"], fontWeight: tokens.fontWeight.semibold, color: tokens.text.muted, mb: 0.25, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                            Requirements
                          </Typography>
                          {req.requirements.map((r, i) => (
                            <Typography key={i} sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.body, pl: 1 }}>
                              {"\u2022"} {r}
                            </Typography>
                          ))}
                        </>
                      )}
                    </Box>
                  </Box>
                ))}
              </Stack>

              <Divider sx={{ mb: 2 }} />

              {/* Notification Options */}
              <Box sx={{ p: 2, bgcolor: tokens.bg.muted, borderRadius: tokens.radius.md, border: `1px solid ${tokens.border.light}` }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={notifyEnabled}
                      onChange={(e) => setNotifyEnabled(e.target.checked)}
                      size="small"
                      sx={{ "& .MuiSwitch-switchBase.Mui-checked": { color: tokens.brand.primary }, "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: tokens.brand.primary } }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.semibold, color: tokens.text.primary }}>
                      Send email notification to supplier
                    </Typography>
                  }
                  sx={{ mb: notifyEnabled ? 1.5 : 0 }}
                />
                {notifyEnabled && (
                  <TextField
                    fullWidth
                    size="small"
                    value={notifyEmail}
                    onChange={(e) => setNotifyEmail(e.target.value)}
                    placeholder="supplier@company.com"
                    InputProps={{ startAdornment: <EmailIcon sx={{ fontSize: 16, color: tokens.text.muted, mr: 1 }} /> }}
                    sx={{ "& .MuiOutlinedInput-root": { fontSize: tokens.fontSize.sm2, borderRadius: tokens.radius.md, bgcolor: tokens.bg.surface } }}
                  />
                )}
                {!notifyEnabled && (
                  <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted, mt: 0.5 }}>
                    Requests will be saved and visible on the supplier portal. No email will be sent.
                  </Typography>
                )}
                {portalLink && (
                  <Button
                    size="small"
                    startIcon={<LinkIcon sx={{ fontSize: 14 }} />}
                    onClick={handleCopyLink}
                    sx={{ mt: 1, textTransform: "none", fontSize: tokens.fontSize["2xs"], color: tokens.text.secondary }}
                  >
                    {linkCopied ? "Portal link copied!" : "Copy supplier portal link"}
                  </Button>
                )}
              </Box>
            </>
          )}
        </DialogContent>
        {!sent && (
          <DialogActions sx={{ px: 3, pb: 2.5 }}>
            <Button onClick={() => setReviewOpen(false)} disabled={sending} sx={{ textTransform: "none", color: tokens.text.secondary }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSend}
              disabled={sending || (notifyEnabled && !notifyEmail.trim())}
              startIcon={sending ? null : <SendIcon sx={{ fontSize: 16 }} />}
              sx={{
                textTransform: "none", fontSize: tokens.fontSize.sm2, fontWeight: tokens.fontWeight.semibold,
                bgcolor: tokens.brand.primary, borderRadius: tokens.radius.md, boxShadow: "none", px: 3,
                "&:hover": { bgcolor: tokens.brand.primaryHover, boxShadow: "none" },
              }}
            >
              {sending ? "Sending..." : notifyEnabled ? "Send & Notify Supplier" : "Save to Portal"}
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* ─── Custom Document Request Dialog ──────────────────────── */}
      <Dialog open={customOpen} onClose={() => setCustomOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: tokens.radius.lg } }}>
        <DialogTitle sx={{ fontSize: tokens.fontSize.lg, fontWeight: tokens.fontWeight.heavy }}>
          Request Custom Document
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField label="Document Name *" value={customLabel} onChange={(e) => setCustomLabel(e.target.value)} fullWidth size="small" placeholder="e.g., Export License, Certificate of Origin" />
            <TextField label="Reason *" value={customReason} onChange={(e) => setCustomReason(e.target.value)} fullWidth size="small" multiline minRows={2} placeholder="Why is this document needed?" />
            <TextField label="Requirements (one per line)" value={customReqs} onChange={(e) => setCustomReqs(e.target.value)} fullWidth size="small" multiline minRows={3} placeholder={"Must show exporter name\nMust be dated within 90 days\nMust be signed"} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCustomOpen(false)} sx={{ textTransform: "none" }}>Cancel</Button>
          <Button variant="contained" onClick={handleAddCustom} disabled={!customLabel.trim() || !customReason.trim()}
            sx={{ textTransform: "none", fontWeight: tokens.fontWeight.semibold, bgcolor: tokens.brand.primary, borderRadius: tokens.radius.md, boxShadow: "none", "&:hover": { bgcolor: tokens.brand.primaryHover, boxShadow: "none" } }}
          >
            Add to Requests
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DocumentRequestBar;
